// Who holds which number, and moving it between organisations.
//
// WHY THIS IS A SEPARATE SCREEN FROM /phones
// There are two number tables and they answer two different questions.
// `phone_numbers` — what /phones edits — is inbound DID routing, mirrored to
// the switches' ivr_dialing_info. `provider_numbers` is OWNERSHIP: it is what
// CallerIdAuthorizer and the outbound caller-ID picker read to decide whether
// an organisation may present a number. Until now the second had no UI at any
// role, so moving a number between tenants meant curl or SQL.
//
// The provider registry at /telephony/providers does list these rows, but its
// allocate/release pair writes `business_id` and `status` — the switch's
// routing label — and never touches `purchased_by_organization_id`. Its "owner"
// column therefore shows the label, which after any real ownership move names
// the PREVIOUS tenant. That is the trap this screen exists to close, so it
// shows the owner and the switch label side by side and says which is which.
//
// PUSH TO SWITCH / RETIRE (NumberSwitchPush.tsx) are the exception to "ownership is
// not routing": push writes the switch label on purpose, behind a preview. Those two
// admit MASTER_ADMIN and PLATFORM_ADMIN, so the page opens for both; assign/release
// stay master-admin only, as below.
//
// PROVIDER VIEW: super_admin / service_admin / provider_owner / provider_admin get
// ProviderNumberOwnership.tsx instead of this screen (their own tree's numbers, Reassign /
// Return to provider via /v1/numbers/provider). Everything below is the master view.
//
// MASTER ADMIN ONLY. Both mutations are @PreAuthorize("hasRole('MASTER_ADMIN')")
// server-side; the gate here is so someone who types the URL gets a straight
// answer instead of a wall of 403s. Reads are broader — the inventory list and
// the history endpoint admit the whole provider-admin set — but a read-only
// version of this page would be a page whose every button fails, so the whole
// screen follows the mutations.
//
// NUMBER LIFECYCLE (2026-10-14): Add numbers, Allot / Reassign (with push to switch, flow
// choice, row create), Release / Retire (with park), Settings and Rollback now go through
// /v1/numbers/lifecycle (NumberLifecycle.tsx). That API admits master/platform admins AND
// admins of the platform root (Green Giraffe); the page asks it (GET /mode) whether this
// user may manage numbers, so a GG admin gets this view while other provider admins keep
// the provider view. The asymmetry note below describes the OLD assign/release, kept for history.
//
// THE ASYMMETRY, stated on the page rather than discovered later: release
// returns a number to the pool and explicitly does NOT remove it from
// ivr_dialing_info. A released number keeps answering inbound calls on the
// switch exactly as it did. That is the single most surprising thing about this
// API, so it is on the page at rest and again inside the confirmation.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Landmark, Loader2, AlertTriangle, Search, Info, RefreshCw, ArrowRightLeft, Undo2,
  History as HistoryIcon, ShieldOff, Check, Lock, X, Send, Archive, ArchiveRestore, Server, Plus, PanelRight,
} from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import { providersService, Provider, ProviderNumber } from 'api/services/providers.service';
import {
  numberOwnershipService, NumberAllocation, LockInTerm,
} from 'api/services/numberOwnership.service';
import { organizationsService, Organization } from 'api/services/organizations.service';
import {
  filterNumbers, holderOf, formatWhen, daysUntil, lockedIn, isHeld, acquireBlock,
  acquireNeedsForce, releaseNeedsForce, byMostRecent, shortId, HolderFilter,
  heldCountInInventory, holdingsAgree,
} from './numberOwnershipRules';
import { isPlatformAdmin, isRetired, toggleId, PARKED_NOTE } from './switchPushRules';
import { switchPushService } from 'api/services/switchPush.service';
import { SwitchPushModal, RetireModal, SwitchHistoryPanel } from './NumberSwitchPush';
import { AddNumbersModal, AllotModal, NumberDetailsDrawer, DrawerTab } from './NumberLifecycle';
import { numberLifecycleService } from 'api/services/numberLifecycle.service';
import { isProviderRole } from './providerReassignRules';
import ProviderNumberOwnership from './ProviderNumberOwnership';

const TERMS: { value: LockInTerm; label: string }[] = [
  { value: 'MONTHLY', label: 'Monthly — 1 month' },
  { value: 'ANNUAL', label: 'Annual — 12 months' },
];

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-[12px] font-medium mb-1" style={{ color: 'var(--text-muted)' }}>{children}</label>
);

interface AssignDraft {
  number: ProviderNumber;
  organizationId: string;
  term: LockInTerm;
  autoRenew: boolean;
  force: boolean;
}

interface ReleaseDraft {
  number: ProviderNumber;
  reason: string;
  force: boolean;
}

const NumberOwnership: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const role = ((currentOrganization as any)?.role || (user as any)?.role || '').toLowerCase();
  // Assign/release are MASTER_ADMIN on the server; push and retire also admit the
  // modern PLATFORM_ADMIN spelling. The page opens for either.
  const canPlatform = isPlatformAdmin(role);
  // GG (root org) admins hold provider roles; the lifecycle API decides whether they may
  // manage numbers. null = still asking.
  const [lifecycleOk, setLifecycleOk] = useState<boolean | null>(canPlatform ? true : null);
  useEffect(() => {
    let live = true;
    if (canPlatform) { setLifecycleOk(true); return () => { live = false; }; }
    numberLifecycleService.mode().then(r => { if (live) setLifecycleOk(!!r.success); });
    return () => { live = false; };
  }, [canPlatform]);
  const canManage = canPlatform || lifecycleOk === true;

  const [numbers, setNumbers] = useState<ProviderNumber[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [holder, setHolder] = useState<HolderFilter>('all');
  const [orgFilter, setOrgFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState<number | ''>('');
  const [showRetired, setShowRetired] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [pushFor, setPushFor] = useState<ProviderNumber[] | null>(null);
  const [retireFor, setRetireFor] = useState<ProviderNumber[] | null>(null);
  const [openSwitchHistory, setOpenSwitchHistory] = useState<string | null>(null);

  const [allotFor, setAllotFor] = useState<ProviderNumber | null>(null);
  const [drawer, setDrawer] = useState<{ id: number; tab?: DrawerTab } | null>(null);
  const [adding, setAdding] = useState(false);
  const [assign, setAssign] = useState<AssignDraft | null>(null);
  // The target organisation's open tenures, fetched when one is picked. This is
  // the other ownership record — the one history is read from — so it is worth
  // seeing before adding to it.
  const [targetHoldings, setTargetHoldings] = useState<NumberAllocation[] | null>(null);
  const [release, setRelease] = useState<ReleaseDraft | null>(null);
  const [busy, setBusy] = useState(false);

  // History is fetched per number, on demand: there is no bulk tenure endpoint,
  // and firing 190 requests to fill a column nobody opened would be worse than
  // the click.
  const [openHistory, setOpenHistory] = useState<string | null>(null);
  const [history, setHistory] = useState<Record<string, NumberAllocation[]>>({});
  const [historyLoading, setHistoryLoading] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [n, services, businesses, provs] = await Promise.all([
      numberOwnershipService.list(),
      organizationsService.getServiceProviders().catch(() => ({ success: false, data: [] } as any)),
      organizationsService.getAllBusinesses().catch(() => ({ success: false, data: [] } as any)),
      providersService.list().catch(() => ({ success: false, data: [] } as any)),
    ]);
    if (n.success) setNumbers(n.data || []);
    else setError(n.error || 'Failed to load the number inventory');

    // Both lists, as on the service-controls page: a service organisation can
    // hold DIDs itself, so restricting the picker to BUSINESS-type rows would
    // hide half the tenants that actually own numbers.
    const all = [...((services as any).data || []), ...((businesses as any).data || [])];
    const byId = new Map<string, Organization>();
    all.forEach((o: Organization) => o?.id && byId.set(o.id, o));
    setOrgs(Array.from(byId.values()).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    setProviders(((provs as any).data || []) as Provider[]);
    setLoading(false);
  }, []);

  useEffect(() => { if (canManage) load(); else if (lifecycleOk === false) setLoading(false); }, [canManage, lifecycleOk, load]);

  const targetOrg = assign?.organizationId || '';
  useEffect(() => {
    let live = true;
    setTargetHoldings(null);
    if (!targetOrg) return () => { live = false; };
    numberOwnershipService.heldBy(targetOrg).then(r => {
      if (live && r.success) setTargetHoldings(r.data || []);
    });
    return () => { live = false; };
  }, [targetOrg]);

  // Keyed by id and carrying businessId, because the switch-label check
  // compares against the org's CODE ("CMC"), not its display name
  // ("CatchMyCall") — comparing names would flag every correct row.
  const orgsById = useMemo(() => {
    const m = new Map<string, Organization>();
    orgs.forEach(o => o.id && m.set(o.id, o));
    return m;
  }, [orgs]);
  const orgName = useCallback((id?: string) => (id && orgsById.get(id)?.name) || undefined, [orgsById]);

  const providerName = (id?: number) => providers.find(p => p.id === id)?.name || (id ? `#${id}` : '—');

  const rows = useMemo(
    () => filterNumbers(numbers, { query, organizationId: orgFilter, providerId: providerFilter, holder, showRetired }),
    [numbers, query, orgFilter, providerFilter, holder, showRetired],
  );

  // A selection only ever holds rows that are on screen: a filter change drops the rest,
  // so "Push 3 numbers" never includes a number the operator can no longer see.
  const visibleIds = useMemo(() => rows.map(n => n.id).filter((x): x is number => typeof x === 'number'), [rows]);
  useEffect(() => { setSelected(sel => sel.filter(id => visibleIds.includes(id))); }, [visibleIds]);
  const selectedRows = useMemo(() => rows.filter(n => typeof n.id === 'number' && selected.includes(n.id)), [rows, selected]);
  const retirable = selectedRows.filter(n => !isRetired(n));
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected.includes(id));

  const doUnretire = async (n: ProviderNumber) => {
    if (typeof n.id !== 'number') return;
    setBusy(true);
    setError(null);
    setNotice(null);
    const r = await switchPushService.unretire(n.id);
    setBusy(false);
    if (r.success) {
      setNotice(`${n.e164} is back in the pool (AVAILABLE, no holder). Its switch row was not changed.`);
      load();
    } else setError(r.error || 'Could not unretire the number');
  };

  /** Owners present in the inventory — including any the picker could not name. */
  const ownersInInventory = useMemo(() => {
    const ids = new Set<string>();
    numbers.forEach(n => { if (n.purchased_by_organization_id) ids.add(String(n.purchased_by_organization_id)); });
    return Array.from(ids)
      .map(id => ({ id, name: orgName(id) || shortId(id), named: orgsById.has(id) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [numbers, orgName, orgsById]);

  const unnamedOwners = ownersInInventory.filter(o => !o.named).length;

  const toggleHistory = async (n: ProviderNumber) => {
    if (openHistory === n.e164) { setOpenHistory(null); return; }
    setOpenHistory(n.e164);
    if (history[n.e164]) return;
    setHistoryLoading(n.e164);
    const r = await numberOwnershipService.history(n.e164);
    setHistoryLoading(null);
    if (r.success) setHistory(h => ({ ...h, [n.e164]: byMostRecent(r.data) }));
    else setError(r.error || 'Could not load this number’s history');
  };

  const doAssign = async () => {
    if (!assign || !assign.organizationId || !assign.number.id) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    const r = await numberOwnershipService.acquire(assign.number.id, {
      organizationId: assign.organizationId,
      term: assign.term,
      autoRenew: assign.autoRenew,
      force: assign.force,
    });
    setBusy(false);
    if (r.success) {
      const name = orgName(assign.organizationId) || assign.organizationId;
      setNotice(
        `${assign.number.e164} now belongs to ${name}. Inbound routing was not changed — ` +
        `if calls to it should now reach ${name}, update the number on Phone Numbers too.`,
      );
      setHistory(h => { const { [assign.number.e164]: _drop, ...rest } = h; return rest; });
      setAssign(null);
      load();
    } else setError(r.error || 'Could not assign the number');
  };

  const doRelease = async () => {
    if (!release || !release.number.id) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    const r = await numberOwnershipService.release(release.number.id, release.reason, release.force);
    setBusy(false);
    if (r.success) {
      setNotice(
        `${release.number.e164} is back in the pool. It is still routing inbound calls on the switch — ` +
        `release does not touch ivr_dialing_info.`,
      );
      setHistory(h => { const { [release.number.e164]: _drop, ...rest } = h; return rest; });
      setRelease(null);
      load();
    } else setError(r.error || 'Could not release the number');
  };

  // Provider roles get their own view: only their tree's numbers, Reassign / Return to
  // provider, read-only history. Nothing below this line is shown to them.
  if (lifecycleOk === null) {
    return (
      <div className="tl-card flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
        <Loader2 className="w-4 h-4 animate-spin" /> Checking access…
      </div>
    );
  }
  if (!canManage && isProviderRole(role)) return <ProviderNumberOwnership />;

  if (!canManage) {
    return (
      <div className="tl-card text-center py-12">
        <ShieldOff className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
        <p className="text-sm font-medium text-gray-900 dark:text-white">Platform admins only</p>
        <p className="text-[13px] mt-1 max-w-md mx-auto" style={{ color: 'var(--text-muted)' }}>
          Moving a number between organisations decides who may present it as caller ID and who is billed
          for it, so it is a platform-level action. The server enforces the same restriction.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
            <Landmark className="w-5 h-5" style={{ color: 'var(--accent)' }} />
            Number ownership
          </h1>
          <p className="text-[13px] mt-1 max-w-2xl" style={{ color: 'var(--text-muted)' }}>
            Which organisation holds each number, on what commitment, and everyone who held it before.
            Ownership is what decides whether an organisation may present a number as outbound caller ID.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="tl-btn tl-btn-primary" onClick={() => setAdding(true)}>
            <Plus className="w-4 h-4" /> Add numbers
          </button>
          <button className="tl-btn tl-btn-secondary" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
        </div>
      </div>

      {/* The asymmetry, at rest. Repeated inside the release confirmation, but
          stated here so it is read before anything is clicked. */}
      <div className="tl-card" style={{ borderLeft: '3px solid var(--amber-label)' }}>
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--amber-label)' }} />
          <div className="text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
            <strong className="text-gray-900 dark:text-white">One place for the whole lifecycle.</strong>{' '}
            Add numbers to a trunk&rsquo;s pool, <strong>Allot</strong> them to any organisation (and, by default, push the
            label and the chosen IVR flow to the number&rsquo;s home switch, creating the row if it is missing), then
            Reassign, Release or Retire with &ldquo;park as FREE&rdquo;. Every switch write is previewed, audited and can be
            rolled back from <strong>Details</strong>. A label billing does not claim is never written: the preview offers
            &ldquo;add it to the billing patterns&rdquo; instead.
          </div>
        </div>
      </div>

      {error && (
        <div className="text-sm rounded-xl px-3 py-2 flex items-start gap-2"
             style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)' }}>
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="text-sm rounded-xl px-3 py-2 flex items-start gap-2"
             style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
          <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{notice}</span>
          <button onClick={() => setNotice(null)} aria-label="Dismiss"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* ── Filters ─────────────────────────────────────────────────────────── */}
      <div className="tl-card">
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <Label>Find a number</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
              <input className="tl-input pl-9" value={query} onChange={e => setQuery(e.target.value)}
                     placeholder="00919228034177, +9122…, or an organisation id" />
            </div>
          </div>
          <div className="min-w-[190px]">
            <Label>Held by</Label>
            <select className="tl-input" value={orgFilter} onChange={e => setOrgFilter(e.target.value)}>
              <option value="">Any organisation</option>
              {ownersInInventory.map(o => (
                <option key={o.id} value={o.id}>{o.name}{o.named ? '' : ' (unnamed)'}</option>
              ))}
            </select>
          </div>
          <div className="min-w-[150px]">
            <Label>Trunk</Label>
            <select className="tl-input" value={providerFilter}
                    onChange={e => setProviderFilter(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Any trunk</option>
              {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="min-w-[150px]">
            <Label>State</Label>
            <select className="tl-input" value={holder} onChange={e => setHolder(e.target.value as HolderFilter)}>
              <option value="all">All</option>
              <option value="held">Held</option>
              <option value="pool">In pool</option>
              <option value="locked">Inside lock-in</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-[12.5px] cursor-pointer pb-2" style={{ color: 'var(--text-muted)' }}>
            <input type="checkbox" checked={showRetired} onChange={e => setShowRetired(e.target.checked)} />
            Show retired
          </label>
        </div>
        <div className="text-[12px] mt-2" style={{ color: 'var(--text-muted)' }}>
          {loading ? 'Loading…' : `${rows.length} of ${numbers.length} numbers`}
          {unnamedOwners > 0 && !loading && (
            <>
              {' · '}
              {unnamedOwners} owner{unnamedOwners === 1 ? '' : 's'} could not be named: the organisation
              picker is scoped to what you may act on, so a number held outside that scope shows its id.
            </>
          )}
        </div>
      </div>

      {/* ── Selection actions ───────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <button className="tl-btn tl-btn-primary tl-btn-sm" disabled={selectedRows.length === 0 || !canPlatform}
                onClick={() => setPushFor(selectedRows)}
                title="Preview, then write each selected number's label on its home switch">
          <Send className="w-3.5 h-3.5" /> Push to switch{selectedRows.length ? ` (${selectedRows.length})` : ''}
        </button>
        <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={retirable.length === 0 || !canPlatform}
                onClick={() => setRetireFor(retirable)}>
          <Archive className="w-3.5 h-3.5" /> Retire{retirable.length ? ` (${retirable.length})` : ''}
        </button>
        {selected.length > 0 && (
          <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={() => setSelected([])}>
            <X className="w-3.5 h-3.5" /> Clear selection
          </button>
        )}
        <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{PARKED_NOTE}</span>
      </div>

      {/* ── Inventory ───────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="tl-card flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading the inventory…
        </div>
      ) : rows.length === 0 ? (
        <div className="tl-card text-center py-10">
          <Landmark className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="text-sm font-medium text-gray-900 dark:text-white">No numbers match</p>
          <p className="text-[13px] mt-1" style={{ color: 'var(--text-muted)' }}>
            {numbers.length === 0 ? 'The inventory is empty.' : 'Clear a filter to widen the search.'}
          </p>
        </div>
      ) : (
        <div className="tl-card tl-card-flush overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[12px]" style={{ color: 'var(--text-muted)' }}>
                <th className="pl-4 py-2.5 w-6">
                  <input type="checkbox" aria-label="Select all shown" checked={allSelected}
                         onChange={() => setSelected(allSelected ? [] : visibleIds)} />
                </th>
                <th className="px-4 py-2.5 font-medium">Number</th>
                <th className="px-4 py-2.5 font-medium">Held by</th>
                <th className="px-4 py-2.5 font-medium">Commitment</th>
                <th className="px-4 py-2.5 font-medium">Trunk</th>
                <th className="px-4 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(n => {
                const h = holderOf(n, orgsById);
                const locked = lockedIn(n);
                const days = daysUntil(n.lock_in_ends_at);
                const expanded = openHistory === n.e164;
                const switchExpanded = openSwitchHistory === n.e164;
                const retired = isRetired(n);
                return (
                  <React.Fragment key={n.id ?? n.e164}>
                    <tr className="border-t" style={{ borderColor: 'var(--border)', opacity: retired ? 0.7 : undefined }}>
                      <td className="pl-4 py-3 align-top">
                        {typeof n.id === 'number' && (
                          <input type="checkbox" aria-label={`Select ${n.e164}`} checked={selected.includes(n.id)}
                                 onChange={() => setSelected(sel => toggleId(sel, n.id as number))} />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {n.e164}
                          {retired && <span className="tl-pill tl-pill-coral ml-1.5">retired</span>}
                        </div>
                        <div className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>
                          id {n.id}
                          {n.label ? ` · ${n.label}` : ''}
                          {n.shared_demo ? ' · shared demo line' : ''}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className={h.organizationId ? 'text-gray-900 dark:text-white' : ''}
                             style={h.organizationId ? undefined : { color: 'var(--text-muted)' }}>
                          {h.label}
                          {!h.named && <span className="tl-pill ml-1.5">id only</span>}
                        </div>
                        {/* Named for what it is. This is the switch's routing label,
                            which acquire never writes — after a move it still says
                            the previous tenant, and reading it as the owner is the
                            mistake the provider registry makes. */}
                        {h.switchLabel && (
                          <div className="text-[11.5px]"
                               style={{ color: h.switchLabelMatchesOwner === false ? 'var(--amber-label)' : 'var(--text-muted)' }}
                               title="business_id — the switch's routing label. Set by the provider registry, never by an ownership move.">
                            switch label: {h.switchLabel}
                            {h.switchLabelMatchesOwner === false && ' — still the previous tenant'}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isHeld(n) ? (
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="tl-pill">{n.lock_in_months === 12 ? 'Annual' : n.lock_in_months ? `${n.lock_in_months} month` : 'No term'}</span>
                              {locked && (
                                <span className="tl-pill tl-pill-amber">
                                  <Lock className="w-3 h-3" /> locked in
                                </span>
                              )}
                              {n.auto_renew === false && <span className="tl-pill tl-pill-coral">no auto-renew</span>}
                            </div>
                            <span className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>
                              {locked ? 'until ' : 'lapsed '}{formatWhen(n.lock_in_ends_at)}
                              {days !== null && locked ? ` · ${days} day${days === 1 ? '' : 's'} left` : ''}
                              {(n.renewal_count || 0) > 0 ? ` · renewed ${n.renewal_count}×` : ''}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[12.5px]" style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
                        {providerName(n.provider_id)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 justify-end flex-wrap">
                          <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={typeof n.id !== 'number'}
                                  title="Holder history, switch rows, billing claim, pushes, settings"
                                  onClick={() => setDrawer({ id: n.id as number })}>
                            <PanelRight className="w-3.5 h-3.5" /> Details
                          </button>
                          <button className="tl-btn tl-btn-secondary tl-btn-sm"
                                  disabled={retired}
                                  title={retired ? 'Retired — unretire it first' : 'Give it to any organisation; push to the switch'}
                                  onClick={() => setAllotFor(n)}>
                            <ArrowRightLeft className="w-3.5 h-3.5" /> {isHeld(n) ? 'Reassign' : 'Allot'}
                          </button>
                          <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={!isHeld(n) || typeof n.id !== 'number'}
                                  title={isHeld(n) ? 'Close the tenure; optionally park the switch row as FREE' : 'Already in the pool'}
                                  onClick={() => setDrawer({ id: n.id as number, tab: 'release' })}>
                            <Undo2 className="w-3.5 h-3.5" /> Release
                          </button>
                          {retired ? (
                            <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={busy}
                                    title="Back to the pool as AVAILABLE, with no holder"
                                    onClick={() => doUnretire(n)}>
                              <ArchiveRestore className="w-3.5 h-3.5" /> Unretire
                            </button>
                          ) : (
                            <button className="tl-btn tl-btn-secondary tl-btn-sm"
                                    title="Garbage number: close the tenure and never allocate it again"
                                    onClick={() => typeof n.id === 'number' && setDrawer({ id: n.id, tab: 'retire' })}>
                              <Archive className="w-3.5 h-3.5" /> Retire
                            </button>
                          )}
                          <button className="tl-icon-btn" title="Switch history"
                                  onClick={() => setOpenSwitchHistory(switchExpanded ? null : n.e164)}>
                            <Server className="w-4 h-4" />
                          </button>
                          <button className="tl-icon-btn" title="Ownership history"
                                  onClick={() => toggleHistory(n)}>
                            {historyLoading === n.e164
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <HistoryIcon className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expanded && (
                      <tr style={{ background: 'var(--surface-raised)' }}>
                        <td colSpan={6} className="px-4 py-3">
                          <div className="text-[12px] font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
                            Everyone who has held {n.e164}
                          </div>
                          {historyLoading === n.e164 ? (
                            <div className="flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                            </div>
                          ) : (history[n.e164] || []).length === 0 ? (
                            <div className="text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
                              No tenures recorded. A number imported straight into inventory has no history
                              until it is assigned through this screen.
                            </div>
                          ) : (
                            <div className="space-y-1.5">
                              {(history[n.e164] || []).map(a => (
                                <div key={a.id} className="text-[12.5px] flex items-start gap-2 flex-wrap">
                                  {/* organizationName is the name AS AT ACQUISITION —
                                      organisations get renamed and the history is
                                      deliberately not updated to follow. */}
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {a.organizationName || shortId(a.organizationId)}
                                  </span>
                                  {a.releasedAt === null
                                    ? <span className="tl-pill tl-pill-success">current holder</span>
                                    : <span className="tl-pill">past</span>}
                                  {a.releasedEarly && <span className="tl-pill tl-pill-amber">released early</span>}
                                  <span style={{ color: 'var(--text-muted)' }}>
                                    {formatWhen(a.acquiredAt)}
                                    {a.acquiredBy ? ` by ${a.acquiredBy}` : ''}
                                    {a.releasedAt !== null
                                      ? ` → ${formatWhen(a.releasedAt)}${a.releasedBy ? ` by ${a.releasedBy}` : ''}`
                                      : ''}
                                    {a.termsServed && a.termsServed > 1 ? ` · ${a.termsServed} terms served` : ''}
                                  </span>
                                  {a.notes && <span className="italic" style={{ color: 'var(--text-muted)' }}>“{a.notes}”</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    {switchExpanded && (
                      <tr style={{ background: 'var(--surface-raised)' }}>
                        <td colSpan={6} className="px-4 py-3">
                          <SwitchHistoryPanel e164={n.e164} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <AddNumbersModal providers={providers} onClose={changed => { setAdding(false); if (changed) load(); }} />
      )}
      {allotFor && (
        <AllotModal number={allotFor} orgs={orgs} onClose={changed => {
          setAllotFor(null);
          if (changed) { setHistory({}); load(); }
        }} />
      )}
      {drawer && (
        <NumberDetailsDrawer numberId={drawer.id} initialTab={drawer.tab} orgs={orgs} onClose={changed => {
          setDrawer(null);
          if (changed) { setHistory({}); setOpenSwitchHistory(null); load(); }
        }} />
      )}
      {pushFor && (
        <SwitchPushModal numbers={pushFor} onClose={changed => {
          setPushFor(null);
          if (changed) { setOpenSwitchHistory(null); load(); }
        }} />
      )}
      {retireFor && (
        <RetireModal numbers={retireFor} onClose={changed => {
          setRetireFor(null);
          if (changed) {
            setSelected([]);
            setHistory({});
            load();
          }
        }} />
      )}

      {/* ── Assign ──────────────────────────────────────────────────────────── */}
      {assign && (() => {
        const block = acquireBlock(assign.number, assign.organizationId);
        const needsForce = acquireNeedsForce(assign.number, assign.organizationId);
        const current = holderOf(assign.number, orgsById);
        const target = orgName(assign.organizationId) || assign.organizationId;
        const blocked = block === 'already-holder' || (needsForce && !assign.force);
        const alreadyHolds = heldCountInInventory(numbers, assign.organizationId);
        const agree = holdingsAgree(alreadyHolds, targetHoldings);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
               style={{ background: 'rgba(0,0,0,.45)' }} onClick={() => !busy && setAssign(null)}>
            <div className="tl-card w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                Assign {assign.number.e164}
              </h2>
              <p className="text-[12.5px] mb-3" style={{ color: 'var(--text-muted)' }}>
                {current.organizationId
                  ? <>Currently held by <strong>{current.label}</strong>. Assigning starts a new committed
                      term for the new holder and closes the current one.</>
                  : <>Currently unowned. Assigning takes it out of the pool and starts a committed term.</>}
              </p>

              <div className="space-y-3">
                <div>
                  <Label>Assign to</Label>
                  <select className="tl-input" value={assign.organizationId}
                          onChange={e => setAssign({ ...assign, organizationId: e.target.value, force: false })}>
                    <option value="">Select an organisation…</option>
                    {orgs.map(o => (
                      <option key={o.id} value={o.id}>{o.name}{o.type === 'BUSINESS' ? ' (client)' : ''}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Term</Label>
                    <select className="tl-input" value={assign.term}
                            onChange={e => setAssign({ ...assign, term: e.target.value as LockInTerm })}>
                      {TERMS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>At the end of the term</Label>
                    <select className="tl-input" value={assign.autoRenew ? 'renew' : 'stop'}
                            onChange={e => setAssign({ ...assign, autoRenew: e.target.value === 'renew' })}>
                      <option value="renew">Renew automatically</option>
                      <option value="stop">Expire and wait for a decision</option>
                    </select>
                  </div>
                </div>
                <p className="text-[12px] -mt-1" style={{ color: 'var(--text-muted)' }}>
                  {assign.autoRenew
                    ? 'The nightly pass rolls the term forward rather than releasing the number — a lapsed DID would otherwise stop taking calls the moment it expired.'
                    : 'The term lapses and the number is reported as awaiting a decision. It is not released automatically.'}
                </p>

                {block === 'already-holder' && (
                  <div className="text-[12.5px] px-3 py-2 rounded-lg flex items-start gap-1.5"
                       style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)' }}>
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>{target}</strong> already holds this number. The server refuses this outright
                      and there is no override for it — pick a different organisation, or release the number
                      first if the intention is to restart its term.
                    </span>
                  </div>
                )}

                {needsForce && (
                  <div className="text-[12.5px] px-3 py-2 rounded-lg"
                       style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
                    <div className="flex items-start gap-1.5">
                      <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong>{current.label} is committed to this number until{' '}
                        {formatWhen(assign.number.lock_in_ends_at)}.</strong>
                        <div className="mt-1">
                          The server refuses the move while that term is running. Overriding ends their
                          term early — the tenure is recorded as released early, which is what a billing
                          dispute is later settled from. It is a decision about a commercial commitment,
                          not a retry.
                        </div>
                        <label className="flex items-start gap-2 cursor-pointer mt-2">
                          <input type="checkbox" className="mt-0.5" checked={assign.force}
                                 onChange={e => setAssign({ ...assign, force: e.target.checked })} />
                          <span>
                            End {current.label}&rsquo;s term early and give the number to {target}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {assign.organizationId && block !== 'already-holder' && agree === false && (
                  <div className="text-[12.5px] px-3 py-2 rounded-lg flex items-start gap-1.5"
                       style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      The two ownership records disagree for {target}: the inventory names it on{' '}
                      {alreadyHolds} number{alreadyHolds === 1 ? '' : 's'}, while it has{' '}
                      {(targetHoldings || []).length} open tenure
                      {(targetHoldings || []).length === 1 ? '' : 's'}. Assigning is still safe — it writes
                      both — but the gap will not repair itself.
                    </span>
                  </div>
                )}

                {assign.organizationId && block !== 'already-holder' && (
                  <div className="text-[12.5px] px-3 py-2 rounded-lg flex items-start gap-1.5"
                       style={{ background: 'var(--surface-raised)', color: 'var(--text-muted)' }}>
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      {alreadyHolds === 0
                        ? <>{target} holds no numbers yet. </>
                        : <>{target} already holds {alreadyHolds} number{alreadyHolds === 1 ? '' : 's'}. </>}
                      This changes ownership only. Calls to {assign.number.e164} will keep arriving wherever
                      the switch already sends them
                      {assign.number.business_id ? <> (currently the <strong>{assign.number.business_id}</strong> flow)</> : null}
                      {' '}until inbound routing is changed on Phone Numbers.
                    </span>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button className="tl-btn tl-btn-secondary" onClick={() => setAssign(null)} disabled={busy}>Cancel</button>
                <button className={`tl-btn ${assign.force ? 'tl-btn-danger' : 'tl-btn-primary'}`}
                        onClick={doAssign} disabled={busy || !assign.organizationId || blocked}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />}
                  {assign.force ? 'Override and assign' : 'Assign'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Release ─────────────────────────────────────────────────────────── */}
      {release && (() => {
        const needsForce = releaseNeedsForce(release.number);
        const current = holderOf(release.number, orgsById);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
               style={{ background: 'rgba(0,0,0,.45)' }} onClick={() => !busy && setRelease(null)}>
            <div className="tl-card w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                Release {release.number.e164}?
              </h2>
              <p className="text-[12.5px] mb-3" style={{ color: 'var(--text-muted)' }}>
                It returns to the pool and <strong>{current.label}</strong> loses it. They can no longer
                present it as outbound caller ID — any agent or campaign configured to dial out from this
                number will be refused at origination.
              </p>

              <div className="text-[12.5px] px-3 py-2 rounded-lg flex items-start gap-1.5 mb-3"
                   style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Inbound keeps working.</strong> Release does not touch{' '}
                  <code>ivr_dialing_info</code>, so calls to {release.number.e164} carry on reaching
                  {release.number.business_id ? <> the <strong>{release.number.business_id}</strong> flow</> : ' the switch'}{' '}
                  exactly as they do now. Stopping that is a separate step on Phone Numbers — and it is
                  usually the wrong one, because a released number is more often re-let than retired.
                </span>
              </div>

              <div className="space-y-3">
                <div>
                  <Label>Reason</Label>
                  <input className="tl-input" value={release.reason}
                         placeholder="e.g. Contract ended — moving to a ported DID"
                         onChange={e => setRelease({ ...release, reason: e.target.value })} />
                  <p className="text-[12px] mt-1" style={{ color: 'var(--text-muted)' }}>
                    Recorded against the closing tenure, where it stays readable after the number is re-let.
                  </p>
                </div>

                {needsForce && (
                  <div className="text-[12.5px] px-3 py-2 rounded-lg"
                       style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)' }}>
                    <div className="flex items-start gap-1.5">
                      <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong>{current.label} is committed until {formatWhen(release.number.lock_in_ends_at)}.</strong>
                        <div className="mt-1">
                          The server refuses a release inside the term. Overriding ends the commitment early
                          and marks the tenure as such in the history.
                        </div>
                        <label className="flex items-start gap-2 cursor-pointer mt-2">
                          <input type="checkbox" className="mt-0.5" checked={release.force}
                                 onChange={e => setRelease({ ...release, force: e.target.checked })} />
                          <span>End the committed term early and release anyway</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button className="tl-btn tl-btn-secondary" onClick={() => setRelease(null)} disabled={busy}>Cancel</button>
                <button className="tl-btn tl-btn-danger" onClick={doRelease}
                        disabled={busy || (needsForce && !release.force)}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
                  {release.force ? 'Override and release' : 'Release to pool'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default NumberOwnership;
